/* eslint-disable sort-exports/sort-exports */
import { z } from "zod";
import { atom, Atom } from "jotai";

import {
  zapFieldMappingRecipe,
  zapFieldMappingToolsAtom,
} from "./recipes/zapRecipe";
import {
  buildZapSkeleton,
  buildZapSkeletonToolsAtom,
} from "./recipes/buildZapSkeleton";

import { JotaiTool } from "../../index";

interface SearchRecipesResult {
  chunk: string;
  similarity: number;
  source: string;
}

export const SearchRecipesToolParams = z.object({
  query: z
    .string()
    .describe(
      "The search query to find similar implementation recipes and code patterns",
    ),
  displayMessage: z
    .string()
    .optional()
    .describe(
      "Short 3-5 word friendly message describing what you're doing, like 'Finding implementation guide...' or 'Loading recipe...'",
    ),
});

export type SearchRecipesToolParamsType = z.infer<
  typeof SearchRecipesToolParams
>;

async function searchSimilarRecipes(
  query: string,
): Promise<SearchRecipesResult[]> {
  try {
    const response = await fetch("/api/canvasui/search-recipes", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        query: query,
      }),
    });

    if (!response.ok) {
      throw new Error(`Search recipes request failed: ${response.statusText}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Search recipes request failed");
    }

    return data.results;
  } catch (error) {
    console.error("Error searching similar recipes:", error);
    throw error;
  }
}

export const searchRecipesToolExecute = async (rawArgs: any) => {
  let params: SearchRecipesToolParamsType;
  try {
    params = SearchRecipesToolParams.parse(rawArgs || {});
  } catch (error) {
    console.error(
      "[SEARCH_RECIPES_TOOL] Error parsing arguments for searchRecipesTool:",
      error,
    );
    return {
      content: [
        {
          type: "text",
          text: `Error: Invalid arguments provided for search_recipes_tool. ${error instanceof Error ? error.message : "Unknown parsing error."}`,
        },
      ],
    };
  }

  const { query } = params;

  try {
    const results = await searchSimilarRecipes(query);
    return {
      content: [
        {
          type: "text",
          // Return only the top 3 results, formatted as pretty JSON
          text: JSON.stringify(results.slice(0, 3), null, 2),
        },
      ],
    };
  } catch (error) {
    console.error(
      "[SEARCH_RECIPES_TOOL] Error in searchRecipesToolExecute:",
      error,
    );
    return {
      content: [
        {
          type: "text",
          text: `Error searching similar recipes: ${error instanceof Error ? error.message : String(error)}`,
        },
      ],
    };
  }
};

// export const searchRecipesToolDescription = `Searches for implementation recipes and code patterns that can help achieve the user's goal.
// - query: The search query to find similar implementation recipes and code patterns - use for specific technical implementations.
// - If you want to discover phase, use the search recipes tool to find specific information that could help with the API calls or execution flow of that step. If an item is returned, prioritize the instructions in that recipe.`;

type Recipes = "zapFieldMapping" | "buildZapSkeleton";

const searchRecipesToolDescription = `
  You can find various recipes for how to do complex operations on Zapier. Please pass in one of the following ENUMS:
  * zapFieldMapping - for how to map fields on a zap
  * buildZapSkeleton - for how to build a zap skeleton
`;

export const searchRecipesToolAtom = atom<
  JotaiTool<z.infer<typeof SearchRecipesToolParams>>
>(() => ({
  name: "search_recipes_tool",
  description: searchRecipesToolDescription,
  parameters: SearchRecipesToolParams,
  execute: atom(null, async (_get, set, { args }) => {
    const query = args.query as keyof typeof recipes;
    if (!query) {
      return {
        content: [{ type: "text", text: "No query provided" }],
      };
    }

    if (!recipes[query]) {
      return {
        content: [{ type: "text", text: "No recipe found for " + query }],
      };
    }

    set(activeRecipeAtom, query);
    return [
      {
        instructions: recipes[query].instructions,
      },
    ];
  }),
}));

export const searchRecipesToolExecuteSchema = z.object({
  query: z
    .string()
    .describe(
      "The search query to find similar implementation recipes and code patterns - use for specific asset names of tables, chatbots, interfaces, zaps, etc.",
    ),
});

export const activeRecipeAtom = atom<keyof typeof recipes | null>();
activeRecipeAtom.debugLabel = "activeRecipeAtom";

export const recipes: Record<
  Recipes,
  { instructions: string; tools: Atom<JotaiTool<any>[]> }
> = {
  zapFieldMapping: {
    instructions: zapFieldMappingRecipe,
    tools: zapFieldMappingToolsAtom,
  },
  buildZapSkeleton: {
    instructions: buildZapSkeleton,
    tools: buildZapSkeletonToolsAtom,
  },
};

searchRecipesToolAtom.debugLabel = "searchRecipesToolAtom";

/* eslint-enable sort-exports/sort-exports */
